import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { ChangeChip } from "@similarweb/ui-components";
import { Box, Stack, Text } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { buildAiTopicKeywordListId } from "app/modules/demand-analysis/keyword-lists";
import { getDefaultParamsForState } from "app/pages/digital-suite/home-page/helpers";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { allTrackers } from "app/services/track/track";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { TopicsDiscoverTrendCardChart } from "./TopicsDiscoverTrendCardChart";
import { TopicsDiscoverTrendCardTitle } from "./TopicsDiscoverTrendCardTitle";
import type { TrendingTopicsData } from "./types";

const CardContainer = styled.div`
    flex: 0 1 calc(25% - 8px);
    overflow: hidden;
    max-width: 25%;
    cursor: pointer;
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    border-radius: 6px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    background: ${colorsPalettes.carbon[0]};
    transition: box-shadow 300ms ease-in-out;

    &:hover {
        box-shadow: 0px 3px 6px 0px rgba(14, 30, 62, 0.08);
        border-color: ${colorsPalettes.carbon[100]};
    }
`;

const volumeFormatter = (value: number) => abbrNumberFilter()(value);
const volumeChangeFormatter = (value: number | null | undefined): string =>
    percentageSignFilter()(Math.abs(value ?? 0), 2);

type TrendingTopic = TrendingTopicsData["topics"][number];
type VolumeChange = TrendingTopic["volumeChange"];
type GrowthValue = NonNullable<VolumeChange>["growth"] | undefined;

const getGrowthSentiment = (growth: GrowthValue): "positive" | "negative" | "neutral" => {
    if (growth === "Growing") {
        return "positive";
    }
    if (growth === "Declining") {
        return "negative";
    }
    return "neutral";
};

export type TopicsDiscoverTrendCardProps = {
    topic: TrendingTopic;
    discoverCategory: string;
};

export const TopicsDiscoverTrendCard = ({ topic, discoverCategory }: TopicsDiscoverTrendCardProps) => {
    const { name, identifier, volume, volumeChange, trend } = topic;

    const handleTopicClick = () => {
        const params = getDefaultParamsForState(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE);
        const isAiTopicIdentifier = identifier.type === "AiTopic" && "seed" in identifier && "country" in identifier;
        const country = isAiTopicIdentifier ? identifier.country : WORLDWIDE_COUNTRY_ID;
        const keywordListId = buildAiTopicKeywordListId(isAiTopicIdentifier ? identifier.seed : name);

        allTrackers.dispatchTrackEvent("Select keyword list", "click", "Discover", {
            target_KW_list_type: "AiTopic",
            target_KW_list_name: name,
            discover_category: discoverCategory,
            target_Clusters: params.showClusters === "true" ? "on" : "off",
        });

        void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
            ...params,
            id: [keywordListId],
            country,
        });
    };

    return (
        <CardContainer
            onClick={handleTopicClick}
            data-automation="card-container"
        >
            <Stack
                width="100%"
                gap="8px"
            >
                <Stack
                    width="100%"
                    gap="16px"
                >
                    <TopicsDiscoverTrendCardTitle name={name} />
                    <Stack
                        gap="4px"
                        alignItems="flex-start"
                    >
                        <Text
                            fontVariant="h4"
                            color={colorsPalettes.carbon[500]}
                            data-automation="card-volume"
                        >
                            {volumeFormatter(volume)}
                        </Text>
                        <ChangeChip
                            data-automation="change-indicator"
                            sentiment={getGrowthSentiment(volumeChange?.growth)}
                            size="small"
                        >
                            {volumeChangeFormatter(volumeChange?.change)}
                        </ChangeChip>
                    </Stack>
                </Stack>
                <Box width="100%">
                    <TopicsDiscoverTrendCardChart trendData={trend} />
                </Box>
            </Stack>
        </CardContainer>
    );
};
